import { Capacitor } from '@capacitor/core';
import { addDays, subDays } from 'date-fns';
import * as Sentry from '@sentry/capacitor';
import { ENV } from 'shared/ENV';
import { isProductionEnvironment } from 'shared/envUtils';
import {
  AppUpdate,
  AppUpdateAvailability,
} from '@capawesome/capacitor-app-update';
import { getAppVersionAgeInMonths } from 'shared/getAppVersionAgeInMonths';
import { TFunction } from 'i18next';
import { getHumanReadableCapacitorPlatform } from 'shared/utils';

export const UPGRADE_LAST_SHOWN_KEY = 'UPGRADE_LAST_SHOWN';
const RECURRING_UPGRADE_MESSAGE_DAYS = 14;

export const isUpgradeAvailable = async (): Promise<boolean> => {
  // Env var makes this easier to test on Android, where the AppUpdate plugin
  // requires a production-signed app to work
  if (ENV.VITE_FORCE_UPDATE_AVAILABLE) {
    return true;
  }

  if (Capacitor.getPlatform() === 'web') {
    return false;
  }

  const updateInfo = await AppUpdate.getAppUpdateInfo();

  return (
    updateInfo.updateAvailability === AppUpdateAvailability.UPDATE_AVAILABLE
  );
};

const getLastUpgradeMessageTime = (): Date | null => {
  try {
    const upgradeTimeObj = JSON.parse(
      localStorage.getItem(UPGRADE_LAST_SHOWN_KEY) || '{}',
    );

    if (upgradeTimeObj.shown) {
      return new Date(upgradeTimeObj.shown);
    }

    return null;
  } catch (e) {
    return null;
  }
};

export const setMessageShownAt = (date: Date): void => {
  localStorage.setItem(UPGRADE_LAST_SHOWN_KEY, JSON.stringify({ shown: date }));
};

export const shouldShowUpgradeMessage = async (
  t: TFunction,
): Promise<{ shouldShow: true; message: string } | { shouldShow: false }> => {
  if (!isProductionEnvironment()) {
    return { shouldShow: false };
  }

  const hasUpdate = await isUpgradeAvailable();

  if (!hasUpdate) {
    // reset counter for next time
    localStorage.removeItem(UPGRADE_LAST_SHOWN_KEY);
    return { shouldShow: false };
  }

  const lastShownTime = getLastUpgradeMessageTime();
  const twoWeeksAgo = subDays(new Date(), RECURRING_UPGRADE_MESSAGE_DAYS);

  if (!lastShownTime) {
    // Give the user 3 days to upgrade before showing the first reminder
    setMessageShownAt(addDays(twoWeeksAgo, 3));
    return { shouldShow: false };
  }

  const shouldShow = lastShownTime < twoWeeksAgo;

  if (!shouldShow) {
    return { shouldShow: false };
  }

  let versionAgeInMonths = 0;

  try {
    versionAgeInMonths = await getAppVersionAgeInMonths();
  } catch (err) {
    Sentry.captureException(err);
  }

  if (versionAgeInMonths >= 18) {
    return { shouldShow: false };
  }

  const message =
    versionAgeInMonths >= 3
      ? t('home.upgrade.expiresSoonMessage', {
          platform: getHumanReadableCapacitorPlatform(),
        })
      : t('home.upgrade.message');

  return { shouldShow: true, message };
};
